import React, { useState, useEffect, useRef, useContext } from "react";
import { TreeNodeModel, CorpContract } from "../tree/types";
import { AddressForm } from "./AddressForm";
import Message from "../ui/Message";
import { AuthContext } from "../../context/auth";
import { ApplicationsContext } from "../../context/applications/ApplicationContext";

interface Props {
  node: TreeNodeModel;
  onSave: (node: TreeNodeModel, data: CorpContract) => void;
  onChange: (node: TreeNodeModel) => void; // ⭐ NUEVO
  readOnly?: boolean;
}

export interface CorpClassifierRow {
  classifier: string;
  value: string;
}

export const CorporationForm: React.FC<Props> = ({
  node,
  onSave,
  onChange,
  readOnly = false
}) => {

  const { user } = useContext(AuthContext);
  const { getAccountOfficers } = useContext(ApplicationsContext);


  const [form, setForm] = useState<CorpContract>({
    id: "",
    clientId: "",
    product: "052-Acq Corporation level",
    contractName: "",
    mcc: "",
    officer: "",
    classifiers: [],
    registrationAddress: {
      addressType: "REGISTRATION_ADDRESS",
      country: user?.country?.id || 166,
      provinceId: "",
      cantonCode: "",
      districtCode: "",
      provinceName: "",
      cantonName: "",
      districtName: "",
      addressLine1: "",
      addressLine2: "",
      contact: {
        firstName: "",
        lastName: "",
        mobilePhone: "",
        businessPhone: "",
        email: "",
      },
      postalCode: "",
    },
    status: "DRAFT",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [officers, setOfficers] = useState([]);
  const [officerValue, setOfficerValue] = useState("");



  const addressRef = useRef<any>(null);
  const loadingRef = useRef(true);


  /* ========================
     LOAD EXISTING DATA
     ======================== */

  useEffect(() => {
    getAccountOfficers().then(setOfficers);
  }, []);

  useEffect(() => {

    if (!node.entityData) return;

    // Solo cargar desde árbol si es primera vez
    if (loadingRef.current) {

      const data = structuredClone(node.entityData as CorpContract);

      if (!data.registrationAddress.country) {
        data.registrationAddress.country = user?.country?.id || 166;
      }

      const officerValue1 =
        data.classifiers?.find(c => c.classifier === "ACCOUNT_OFFICER")?.value || "";

      setOfficerValue(officerValue1);
      setForm(data);


      loadingRef.current = false;
    }

  }, [node.id]);

  useEffect(() => {
    loadingRef.current = true;
  }, [node.id]);


  /* ========================
     LIVE UPDATE TREE
     ======================== */

  const updateLive = (updatedForm: CorpContract) => {

    setForm(updatedForm);

    onChange?.({
      ...node,
      name: updatedForm.contractName || node.name || "Corporación",
      entityData: updatedForm,
      saved: false
    });

  };


  /* ========================
     SAVE TO DB
     ======================== */

  const handleSave = () => {

    if (readOnly) return;

    if (!form.contractName.trim()) {
      setErrorMessage("Por favor complete todos los campos requeridos.");
      return;
    }

    const isAddressValid = addressRef.current?.validate();

    if (!isAddressValid) return;

    const updatedNode: TreeNodeModel = {
      ...node,
      name: form.contractName,
      saved: true,
      entityData: form,
    };

    onSave(updatedNode, form);
  };


  const removeError = () => setErrorMessage("");




  /* ========================
     RENDER
     ======================== */

  return (

    <div className="container-fluid">

      <h5 className="mb-3">
        Corporación
      </h5>


      <div className="mb-3">

        <label className="form-label">
          Producto
        </label>

        <input
          className="form-control"
          value="Nivel Corporativo"
          disabled
        />

      </div>


      <div className="mb-3">

        <label className="form-label">
          Nombre de la Corporación *
        </label>

        <input
          className="form-control"
          value={form.contractName}
          disabled={readOnly}
          onChange={(e) =>
            updateLive({
              ...form,
              contractName: e.target.value
            })
          }
        />

      </div>

      {
        user?.isLafise && (
          <div className="mb-3">
            <label className="form-label">Ejecutivo de cuenta </label>
            <select
              name="officer"
              className="form-select"
              value={officerValue}
              onChange={(e) => {
                setOfficerValue(e.target.value);
                updateLive({
                  ...form,
                  classifiers: [
                    {
                      classifier: "ACCOUNT_OFFICER",
                      value: e.target.value
                    }

                  ]
                })
              }

              }
              disabled={readOnly}
            >
              <option value="">Seleccione Ejecutivo de Cuenta</option>
              {officers.sort((a, b) => a.valueName.localeCompare(b.valueName)).map(c => (
                <option key={c.classifierId} value={c.valueCode}>
                  {c.valueName}
                </option>
              ))}
            </select>
          </div>
        )
      }


      <AddressForm
        ref={addressRef}
        key={node.id}
        title="Dirección de Registro"
        addressType="REGISTRATION_ADDRESS"
        data={form.registrationAddress}
        readOnly={readOnly}
        onChange={(data) =>
          updateLive({
            ...form,
            registrationAddress: data
          })
        }
      />


      {!readOnly && (

        <button
          className="btn btn-primary mt-3"
          onClick={handleSave}
        >
          Guardar Corporación
        </button>

      )}


      {errorMessage && (

        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />

      )}

    </div>
  );
};